import React, { useState, useEffect } from 'react';
import { Select, Input, Form } from 'antd';
import PropTypes from 'prop-types';
import {
  getVariantKeysForBusinessType,
  getVariantValuesForKey,
  CUSTOM_OPTION,
} from '../../constants/variantPresets';
import strings from '../../strings';

const FormItem = Form.Item;
const Option = Select.Option;

/**
 * VariantKeySelector Component
 * Provides a dropdown to select variant keys with custom input option
 */
export const VariantKeySelector = ({
  businessType = 'GENERAL',
  value,
  onChange,
  placeholder = 'Select or enter variant key',
  size = 'large',
  disabled = false,
}) => {
  const [customKey, setCustomKey] = useState('');
  const [isCustom, setIsCustom] = useState(
    value && !getVariantKeysForBusinessType(businessType).some(k => k.value === value)
  );

  const variantKeys = getVariantKeysForBusinessType(businessType);

  useEffect(() => {
    // Check if current value is custom (not in presets)
    const isCustomValue = value && !variantKeys.some(k => k.value === value);
    setIsCustom(isCustomValue);
    if (isCustomValue) {
      setCustomKey(value);
    }
  }, [value, businessType, variantKeys]);

  const handleChange = (val) => {
    if (val === 'CUSTOM') {
      setIsCustom(true);
      setCustomKey('');
    } else {
      setIsCustom(false);
      onChange(val);
    }
  };

  const handleCustomChange = (e) => {
    const val = e.target.value;
    setCustomKey(val);
    onChange(val);
  };

  if (isCustom) {
    return (
      <Input
        size={size}
        placeholder="Enter custom value"
        value={customKey}
        onChange={handleCustomChange}
        disabled={disabled}
      />
    );
  }

  return (
    <Select
      size={size}
      placeholder={placeholder}
      value={value || undefined}
      onChange={handleChange}
      disabled={disabled}
      allowClear
    >
      {variantKeys.map(key => (
        <Option key={key.value} value={key.value}>
          {key.label}
        </Option>
      ))}
      <Option value="CUSTOM">{CUSTOM_OPTION.label}</Option>
    </Select>
  );
};

VariantKeySelector.propTypes = {
  businessType: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
};

/**
 * VariantValueSelector Component
 * Provides a dropdown to select variant values based on the selected key
 * with custom input option
 */
export const VariantValueSelector = ({
  businessType = 'GENERAL',
  variantKey,
  value,
  onChange,
  placeholder = 'Select or enter variant value',
  size = 'large',
  disabled = false,
}) => {
  const [customValue, setCustomValue] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  const variantValues = variantKey
    ? getVariantValuesForKey(businessType, variantKey)
    : [];

  useEffect(() => {
    // Check if current value is custom (not in presets for the selected key)
    const isCustomVal =
      value &&
      variantKey &&
      !variantValues.some(v => v.value === value);
    setIsCustom(isCustomVal);
    if (isCustomVal) {
      setCustomValue(value);
    }
  }, [value, variantKey, businessType, variantValues]);

  const handleChange = (val) => {
    if (val === 'CUSTOM') {
      setIsCustom(true);
      setCustomValue('');
    } else {
      setIsCustom(false);
      onChange(val);
    }
  };

  const handleCustomChange = (e) => {
    const val = e.target.value;
    setCustomValue(val);
    onChange(val);
  };

  if (!variantKey) {
    return (
      <Input
        size={size}
        placeholder="Select an option first"
        disabled
      />
    );
  }

  // If no preset values exist for this key, show custom input
  if (variantValues.length === 0) {
    return (
      <Input
        size={size}
        placeholder="Enter custom value"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    );
  }

  if (isCustom) {
    return (
      <Input
        size={size}
        placeholder="Enter custom value"
        value={customValue}
        onChange={handleCustomChange}
        disabled={disabled}
      />
    );
  }

  return (
    <Select
      size={size}
      placeholder={placeholder}
      value={value || undefined}
      onChange={handleChange}
      disabled={disabled}
      allowClear
    >
      {variantValues.map(val => (
        <Option key={val.value} value={val.value}>
          {val.label}
        </Option>
      ))}
      <Option value="CUSTOM">{CUSTOM_OPTION.label}</Option>
    </Select>
  );
};

VariantValueSelector.propTypes = {
  businessType: PropTypes.string,
  variantKey: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
};

export default {
  VariantKeySelector,
  VariantValueSelector,
};
